import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useState } from "react";
import ContasService from "services/accounts";
import FinanceiroService from "services/financeiro";
import NaturezaService from "services/sub-group-accounts";
import { ptBR_DataGrid } from "utils/datagrid";
import { dataCalendario, hojeISO } from "utils/dataCalendario";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

const SITUACAO = {
  pendente: { rotulo: "Pendente", cor: "warning" },
  conciliado: { rotulo: "Conciliado", cor: "success" },
  ignorado: { rotulo: "Ignorado", cor: "default" },
};

const ORIGEM = {
  lote: "Lote de baixa",
  conta: "Pagamento da conta",
  manual: "Lançamento manual",
};

// dia 1 de n meses atrás ("AAAA-MM-DD", fuso local)
const mesesAtras = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

/**
 * Extrato bancário (OFX): importa o arquivo do banco, concilia sozinho o que
 * tem par exato no sistema e deixa o resto para resolver aqui: conciliar com
 * um movimento, virar lançamento manual (tarifa, rendimento...) ou ignorar.
 */
export default function ExtratoBancario() {
  const [contas, setContas] = useState([]);
  const [naturezas, setNaturezas] = useState([]);
  const [account, setAccount] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [usarSaldo, setUsarSaldo] = useState(true);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [situacao, setSituacao] = useState("pendente");
  const [inicio, setInicio] = useState(mesesAtras(2));
  const [fim, setFim] = useState(hojeISO());
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [conciliando, setConciliando] = useState(null); // { linha, candidatos }
  const [lancando, setLancando] = useState(null); // { linha, historico, natureza }

  useEffect(() => {
    ContasService.list()
      .then(({ data }) => setContas(data || []))
      .catch(() => {});
    NaturezaService.list()
      .then(({ data }) => setNaturezas(data || []))
      .catch(() => {});
  }, []);

  const buscar = async () => {
    setLoading(true);
    try {
      const lista = await FinanceiroService.extrato({
        account,
        situacao,
        dataInicio: inicio,
        dataFim: fim,
      });
      setLinhas(lista.map((l) => ({ ...l, id: l._id })));
    } catch (e) {
      setErro(e?.error || "Erro ao carregar o extrato.");
    }
    setLoading(false);
  };

  useEffect(() => {
    buscar();
  }, [account, situacao, inicio, fim]);

  const importar = async () => {
    setErro("");
    setResultado(null);
    if (!account) return setErro("Escolha a conta bancária do extrato.");
    if (!arquivo) return setErro("Escolha o arquivo OFX.");
    setImportando(true);
    try {
      setResultado(
        await FinanceiroService.importarOfx(arquivo, account, usarSaldo)
      );
      buscar();
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao importar o extrato.");
    }
    setImportando(false);
  };

  const acao = async (fn) => {
    setErro("");
    try {
      await fn();
      buscar();
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao executar a ação.");
    }
  };

  const abrirConciliacao = async (linha) => {
    setConciliando({ linha, candidatos: null });
    try {
      setConciliando({
        linha,
        candidatos: await FinanceiroService.candidatos(linha._id),
      });
    } catch (e) {
      setConciliando(null);
      setErro(e?.error || "Erro ao buscar candidatos.");
    }
  };

  const filtro = { size: "small", InputLabelProps: { shrink: true } };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Extrato bancário (OFX)
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            <Typography variant="subtitle2" mb={1}>
              Importar extrato
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                label="Conta bancária"
                select
                SelectProps={{ native: true }}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                sx={{ minWidth: 220 }}
                {...filtro}
              >
                <option value="">Escolha</option>
                {contas.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.nome}
                    {c.numeroConta ? ` (${c.numeroConta})` : ""}
                  </option>
                ))}
              </TextField>
              <Button
                variant="outlined"
                component="label"
                sx={{ color: "text.primary" }}
              >
                {arquivo ? arquivo.name : "Escolher arquivo .ofx"}
                <input
                  hidden
                  type="file"
                  accept=".ofx,.OFX"
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                />
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={usarSaldo}
                    onChange={(e) => setUsarSaldo(e.target.checked)}
                  />
                }
                label="Usar o saldo do extrato como saldo informado"
              />
              <Button
                variant="contained"
                sx={{ color: "white !important" }}
                disabled={importando}
                onClick={importar}
              >
                {importando ? "Importando..." : "Importar"}
              </Button>
            </Box>

            {resultado && (
              <Alert
                severity={resultado.avisos?.length ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                {resultado.transacoes} transações no arquivo: {resultado.novas}{" "}
                novas, {resultado.jaImportadas} já importadas.{" "}
                {resultado.conciliadasAutomaticamente} conciliadas
                automaticamente; {resultado.pendentesNaConta} pendentes nesta
                conta.
                {resultado.saldo &&
                  ` Saldo do extrato: ${brl(
                    resultado.saldo.valor
                  )} em ${dataCalendario(resultado.saldo.data)}.`}
                {resultado.avisos?.map((a) => (
                  <div key={a}>
                    <b>{a}</b>
                  </div>
                ))}
              </Alert>
            )}
            {erro && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setErro("")}
              >
                {erro}
              </Alert>
            )}

            <Box display="flex" gap={2} flexWrap="wrap" mt={4} mb={2}>
              <TextField
                label="Situação"
                select
                SelectProps={{ native: true }}
                value={situacao}
                onChange={(e) => setSituacao(e.target.value)}
                sx={{ minWidth: 150 }}
                {...filtro}
              >
                <option value="">Todas</option>
                <option value="pendente">Pendentes</option>
                <option value="conciliado">Conciliadas</option>
                <option value="ignorado">Ignoradas</option>
              </TextField>
              <TextField
                label="De"
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                {...filtro}
              />
              <TextField
                label="Até"
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                {...filtro}
              />
            </Box>

            <DataGrid
              rows={linhas}
              autoHeight
              density="compact"
              loading={loading}
              localeText={ptBR_DataGrid}
              rowSelection={false}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[50, 100]}
              columns={[
                {
                  field: "data",
                  headerName: "Data",
                  minWidth: 100,
                  valueGetter: ({ row }) => dataCalendario(row.data),
                },
                {
                  field: "conta",
                  headerName: "Conta",
                  minWidth: 120,
                  valueGetter: ({ row }) => row.account?.nome,
                },
                {
                  field: "descricao",
                  headerName: "Descrição no banco",
                  flex: 1.6,
                },
                {
                  field: "valor",
                  headerName: "Valor",
                  minWidth: 120,
                  align: "right",
                  headerAlign: "right",
                  renderCell: ({ row }) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color: row.valor < 0 ? "error.dark" : "success.dark",
                      }}
                    >
                      {brl(row.valor)}
                    </Typography>
                  ),
                },
                {
                  field: "situacao",
                  headerName: "Situação",
                  minWidth: 150,
                  renderCell: ({ row }) => (
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={SITUACAO[row.situacao]?.rotulo}
                        color={SITUACAO[row.situacao]?.cor}
                      />
                      {row.conciliadoAutomaticamente && (
                        <Typography variant="caption" color="text.secondary">
                          auto
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  field: "acoes",
                  headerName: "",
                  minWidth: 300,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Box display="flex" gap={1}>
                      {row.situacao === "pendente" && (
                        <>
                          <Button
                            size="small"
                            onClick={() => abrirConciliacao(row)}
                          >
                            Conciliar
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              setLancando({
                                linha: row,
                                historico: row.descricao || "",
                                natureza: "",
                              })
                            }
                          >
                            Lançar
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              acao(() =>
                                FinanceiroService.ignorarLinha(row._id, true)
                              )
                            }
                          >
                            Ignorar
                          </Button>
                        </>
                      )}
                      {row.situacao === "conciliado" && (
                        <Button
                          size="small"
                          onClick={() =>
                            acao(() => FinanceiroService.desfazerLinha(row._id))
                          }
                        >
                          Desfazer
                        </Button>
                      )}
                      {row.situacao === "ignorado" && (
                        <Button
                          size="small"
                          onClick={() =>
                            acao(() =>
                              FinanceiroService.ignorarLinha(row._id, false)
                            )
                          }
                        >
                          Voltar a pendente
                        </Button>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          </MDBox>
        </Card>

        {/* Conciliar com um movimento do sistema */}
        <Dialog
          open={Boolean(conciliando)}
          onClose={() => setConciliando(null)}
          maxWidth="md"
          fullWidth
          sx={{ zIndex: 10000 }}
        >
          <DialogTitle>Conciliar linha do extrato</DialogTitle>
          <DialogContent>
            {conciliando && (
              <Typography variant="body2" mb={2}>
                {dataCalendario(conciliando.linha.data)} ·{" "}
                {conciliando.linha.descricao} ·{" "}
                <b>{brl(conciliando.linha.valor)}</b>
              </Typography>
            )}
            {conciliando?.candidatos === null && (
              <Typography variant="body2">Buscando...</Typography>
            )}
            {conciliando?.candidatos?.length === 0 && (
              <Alert severity="info">
                Nenhum movimento livre nesta conta perto dessa data. Se for
                tarifa ou rendimento, use &quot;Lançar&quot;; se for um
                pagamento, confira se a conta foi baixada com a conta bancária
                certa.
              </Alert>
            )}
            {conciliando?.candidatos?.length > 0 && (
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conciliando.candidatos.map((c) => (
                    <TableRow
                      key={c.id}
                      sx={{
                        backgroundColor: c.valorIgual
                          ? "rgba(0,0,0,0.03)"
                          : undefined,
                      }}
                    >
                      <TableCell>{dataCalendario(c.data)}</TableCell>
                      <TableCell>
                        {[c.descricao, c.pessoa].filter(Boolean).join(" · ")}
                      </TableCell>
                      <TableCell>{ORIGEM[c.origem]}</TableCell>
                      <TableCell align="right">
                        {brl(c.valor)}
                        {!c.valorIgual && (
                          <Typography
                            variant="caption"
                            color="error.dark"
                            display="block"
                          >
                            valor diferente
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() =>
                            acao(async () => {
                              await FinanceiroService.conciliarLinha(
                                conciliando.linha._id,
                                c.id
                              );
                              setConciliando(null);
                            })
                          }
                        >
                          Escolher
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConciliando(null)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Virar lançamento manual */}
        <Dialog
          open={Boolean(lancando)}
          onClose={() => setLancando(null)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 10000 }}
        >
          <DialogTitle>Lançar a partir do extrato</DialogTitle>
          <DialogContent>
            {lancando && (
              <>
                <Typography variant="body2" mb={2}>
                  {lancando.linha.valor < 0 ? "Saída" : "Entrada"} de{" "}
                  <b>{brl(Math.abs(lancando.linha.valor))}</b> em{" "}
                  {dataCalendario(lancando.linha.data)}, já conciliada.
                </Typography>
                <TextField
                  label="Histórico"
                  value={lancando.historico}
                  onChange={(e) =>
                    setLancando({ ...lancando, historico: e.target.value })
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Natureza de lançamento"
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  value={lancando.natureza}
                  onChange={(e) =>
                    setLancando({ ...lancando, natureza: e.target.value })
                  }
                  fullWidth
                >
                  <option value=""></option>
                  {naturezas.map((n) => (
                    <option key={n._id} value={n._id}>
                      {n.codigo ? `${n.codigo} - ` : ""}
                      {n.nome}
                    </option>
                  ))}
                </TextField>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLancando(null)}>Cancelar</Button>
            <Button
              variant="contained"
              sx={{ color: "white !important" }}
              onClick={() =>
                acao(async () => {
                  await FinanceiroService.lancarLinha(lancando.linha._id, {
                    historico: lancando.historico,
                    natureza: lancando.natureza,
                  });
                  setLancando(null);
                })
              }
            >
              Lançar
            </Button>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
